import { type Idl, utils } from '@coral-xyz/anchor';
import {
  Blockhash,
  ComputeBudgetProgram,
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  VersionedTransaction,
  VersionedTransactionResponse,
} from '@solana/web3.js';
import { COMMON_TX_ERRORS, ParseErrorParam, TransactionError, parseErrorForTransaction } from './errors';
import { JitoResponse, transactionSenderAndConfirmationWaiter } from './poll';

export type BROADCAST_MODE = 'priorityFees' | 'mixed' | 'jito';

interface IHandleSendTransactionOptions {
  connection: Connection;
  signedTransaction: VersionedTransaction | Transaction;
  blockhash: Blockhash;
  lastValidBlockHeight: number;
  skipPreflight?: boolean;
  idl?: Idl;
  idlProgramId?: PublicKey;
  minContextSlot?: number;
  broadcastMode?: BROADCAST_MODE;
  jitoTipTx?: Transaction | VersionedTransaction;
  jitoProxyUrl?: string;
  debug?: boolean;
}

export function getSignature(transaction: Transaction | VersionedTransaction): string {
  const signature = 'signature' in transaction ? transaction.signature : transaction.signatures[0];
  if (!signature) {
    throw new Error('Missing transaction signature, the transaction was not signed by the fee payer');
  }
  return utils.bytes.bs58.encode(signature);
}

interface IHandleSendTransactionError {
  error: TransactionError;
  transactionResponse?: VersionedTransactionResponse;
  txid?: string;
}

interface IHandleSendTransactionResponse {
  transactionResponse: VersionedTransactionResponse;
  txid: string;
  jitoResponse: JitoResponse | null;
}

export const handleSendTransaction = async ({
  connection,
  signedTransaction,
  blockhash,
  lastValidBlockHeight,
  idl,
  skipPreflight,
  idlProgramId,
  minContextSlot,
  broadcastMode,
  jitoTipTx,
  jitoProxyUrl,
  debug,
}: IHandleSendTransactionOptions): Promise<IHandleSendTransactionError | IHandleSendTransactionResponse> => {
  let serializedTransaction: Buffer = Buffer.from(signedTransaction.serialize());

  let signature = getSignature(signedTransaction);

  const { value: simulatedTransactionResponse } = await connection.simulateTransaction(
    'message' in signedTransaction ? signedTransaction : new VersionedTransaction(signedTransaction.compileMessage()),
    {
      replaceRecentBlockhash: true,
      commitment: 'processed',
      minContextSlot,
    },
  );

  const { err, logs } = simulatedTransactionResponse;

  if (debug) {
    console.log('Simulated Transaction Logs:', logs);
  }

  if (err) {
    // dont save txid to error cause it is not sent
    const error = parseErrorWithIdl({ err, logMessages: logs }, undefined, idl, idlProgramId);

    if (skipPreflight) {
      if (
        error.message === COMMON_TX_ERRORS.INSUFFICIENT_FUNDS_FOR_RENT ||
        error.message === COMMON_TX_ERRORS.TOO_MANY_ACCOUNT_LOCKS
      ) {
        error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
        return {
          error,
          txid: undefined,
        };
      }
    } else {
      error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');
      return {
        error,
        txid: undefined,
      };
    }
  }

  const { response: transactionResponse, jitoResponse } = await transactionSenderAndConfirmationWaiter({
    txid: signature,
    connection,
    serializedTransaction,
    blockhashWithBlockHeight: {
      blockhash,
      lastValidBlockHeight,
    },
    broadcastMode,
    jitoTipTx,
    jitoProxyUrl,
  });

  if (!transactionResponse) {
    const error = new TransactionError(COMMON_TX_ERRORS.TRANSACTION_NOT_CONFIRMED);
    error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');

    return {
      error,
      txid: undefined,
    };
  }

  if (transactionResponse.meta?.err) {
    const error = parseErrorWithIdl(transactionResponse.meta, signature, idl, idlProgramId);
    error.serializedTx = Buffer.from(signedTransaction.serialize()).toString('base64');

    return {
      error,
      transactionResponse,
      txid: signature,
    };
  }

  return { transactionResponse, jitoResponse, txid: signature };
};

export const parseErrorWithIdl = (errorParams: ParseErrorParam, txid?: string, idl?: Idl, idlProgramId?: PublicKey) => {
  const { code, programIds, message } = parseErrorForTransaction(errorParams);

  if (idlProgramId && idl) {
    if (programIds?.includes(idlProgramId.toBase58())) {
      let error = idl.errors?.find((error) => {
        return error.code === code;
      });

      if (error) {
        // msg might not be exposed by IDL, so we fallback to name
        return new TransactionError(error.msg ?? error.name, txid, error.code, programIds);
      }
    }
  }

  return new TransactionError(message, txid, code, programIds);
};

const JITO_TIP_ACCOUNTS = [
  '96gYZGLnJYVFmbjzopPSU6QiEV5fGqZNyN9nmNhvrZU5',
  'HFqU5x63VTqvQss8hp11i4wVV8bD44PvwucfZ2bU7gRe',
  'Cw8CFyM9FkoMi7K7Crf6HNQqf4uEMzpKw6QNghXLvLkY',
  'ADaUMid9yfUytqMBgopwjb2DTLSokTSzL1zt6iGPaS49',
  'DfXygSm4jCyNCybVYYK6DwvWqjKee8pbDmJGcLWNDXjh',
  'ADuUkR4vqLUMWXxW9gh6D6L8pMSawimctcNZ5pGwDcEt',
  'DttWaMuVvTiduZRnguLF7jNxTgiMBZ1hyAumKUiL2KRL',
  '3AVi9Tg9Uo68tJfuvoKvqKNWKkC5wPdSSdeBnizKZ6jT',
];

export const getRandomTipAccount = (): PublicKey =>
  new PublicKey(JITO_TIP_ACCOUNTS[Math.floor(Math.random() * JITO_TIP_ACCOUNTS.length)]);

export function createJitoTipTx({
  payer,
  lamports,
  blockhash,
  lastValidBlockHeight,
}: {
  payer: PublicKey;
  lamports: string;
  blockhash: Blockhash;
  lastValidBlockHeight: number;
}) {
  const tipAccount = getRandomTipAccount();
  const tipIx = SystemProgram.transfer({
    fromPubkey: payer,
    toPubkey: tipAccount,
    lamports: BigInt(lamports),
  });

  const tx = new Transaction({ blockhash, lastValidBlockHeight }).add(tipIx);
  tx.add(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 0 }));
  tx.feePayer = payer;

  return tx;
}
